import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useProvisionTokenMutation } from 'network/mutations/useProvisionTokenMutation';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { prependHttpsIfNeeded } from '../../GithubManifestInstallationDialog/utils';
import { CONFIGURE_TOKEN_FIELDS, FORM_IDS } from '../consts';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { getConfigureTokenFormSchema } from '../schemas';
import { ConfigureTokenFormData } from '../types';
import { splitGitlabUrlLines } from '../utils';

const GROUP_STATUS_CREATED = 'created';

/**
 * Token-first onboarding step (Option A). The user pastes a service-account
 * access token (PAT / group access token) plus one group URL per line; we call
 * `provision-token` synchronously (no OAuth redirect) and render the per-group
 * outcome inline. Next is enabled once at least one group connects.
 */
export const ConfigurePatToken = () => {
  const { setIsPending, goNext } = useMultiStepDialog();
  const { setTotalSucceededCount } = useGitlabInstallation();
  const {
    mutate: provisionToken,
    data,
    isPending,
  } = useProvisionTokenMutation();

  const spawnStep = spawn('dialogs.gitlab-installation.configure-token');

  const methods = useForm<ConfigureTokenFormData>({
    resolver: zodResolver(getConfigureTokenFormSchema()),
    defaultValues: {
      [CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN]: '',
      [CONFIGURE_TOKEN_FIELDS.GITLAB_URLS]: '',
    },
  });

  const formClassName = cn(
    "[&_[data-slot='form-label']]:font-mono",
    "[&_[data-slot='form-label']]:text-xs",
    "[&_[data-slot='form-label']]:leading-[140%]",
    "[&_[data-slot='form-description']]:text-text-bg-neutral-mild",
  );

  useEffect(() => {
    setIsPending(isPending);
  }, [isPending]);

  const onSubmit = (formData: ConfigureTokenFormData) => {
    const urls = splitGitlabUrlLines(formData.gitlabUrls).map(
      prependHttpsIfNeeded,
    );

    provisionToken(
      {
        accessToken: formData.accessToken,
        gitlabUrls: urls,
      },
      {
        // Mirror the OAuth ConfigureApp flow: the dialog's Next button submits
        // this form, so advance on success. If every group failed we stay and
        // render the per-group results inline (the error toast covers transport
        // failures like an invalid token).
        onSuccess: (result) => {
          // Feed the final "N connected" count to EnableQodo via context;
          // otherwise the success step renders 0 for the PAT flow.
          setTotalSucceededCount(result.succeededCount);

          if (result.succeededCount > 0) {
            goNext();
          }
        },
      },
    );
  };

  return (
    <Step title={spawnStep('title')} description={spawnStep('description')}>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.CONFIGURE_TOKEN } as Record<string, string>)}
      >
        <Form.Textarea
          name={CONFIGURE_TOKEN_FIELDS.GITLAB_URLS}
          label={spawnStep('gitlab-urls.label')}
          placeholder={spawnStep('gitlab-urls.placeholder')}
          description={spawnStep('gitlab-urls.description')}
        />
        <Form.Input
          type="password"
          name={CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN}
          label={spawnStep('access-token.label')}
          placeholder={spawnStep('access-token.placeholder')}
        />
      </Form>

      {data && (
        <div className="mt-4 flex flex-col gap-4">
          <InstallationStatusMessage
            status={data.failedCount === 0 ? 'success' : 'error'}
            message={
              data.failedCount === 0
                ? spawnStep('results.success', { count: data.succeededCount })
                : spawnStep('results.partial-failure', {
                    succeeded: data.succeededCount,
                    failed: data.failedCount,
                  })
            }
          />
          <ul className="flex flex-col gap-1.5">
            {data.results.map((result) => {
              const created = result.status === GROUP_STATUS_CREATED;

              return (
                <li
                  key={result.groupPath}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <span className="text-text-general-neutral-strong">
                    {result.groupPath}
                  </span>
                  <span
                    className={cn(
                      'shrink-0',
                      created
                        ? 'text-text-tag-positive-active'
                        : 'text-text-tag-negative-active',
                    )}
                  >
                    {created
                      ? spawnStep('results.group-status.created')
                      : result.error ??
                        spawnStep('results.group-status.failed')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Step>
  );
};
